import React, { useEffect, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import './PDFModal.css';

interface PDFModalProps {
    isOpen: boolean;
    onClose: () => void;
    pdfDirectory?: string;
    pageCount?: number;
    title?: string;
    content?: string;
}

const PDFModal: React.FC<PDFModalProps> = ({ isOpen, onClose, pdfDirectory, pageCount, title, content }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pages, setPages] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [displayContainer, setDisplayContainer] = useState<HTMLDivElement | null>(null);
    const [scrollProgress, setScrollProgress] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStartY, setDragStartY] = useState(0);
    const [dragStartScroll, setDragStartScroll] = useState(0);
    const [scrollbarHeight, setScrollbarHeight] = useState(400);
    const [previewScrollbarHeight, setPreviewScrollbarHeight] = useState(400);
    const [previewScrollProgress, setPreviewScrollProgress] = useState(0);
    const [isPreviewDragging, setIsPreviewDragging] = useState(false);
    const [previewDragStartY, setPreviewDragStartY] = useState(0);
    const [previewDragStartScroll, setPreviewDragStartScroll] = useState(0);
    const [previewContainer, setPreviewContainer] = useState<HTMLDivElement | null>(null);
    const [isNavigating, setIsNavigating] = useState(false);
    const [navigationTimeout, setNavigationTimeout] = useState<NodeJS.Timeout | null>(null);
    const [previewNeedsScroll, setPreviewNeedsScroll] = useState(false);
    const [imagesLoaded, setImagesLoaded] = useState(0);
    const [allImagesLoaded, setAllImagesLoaded] = useState(false);


    useEffect(() => {
        if (isOpen && pdfDirectory && pageCount) {
            setImagesLoaded(0);
            setAllImagesLoaded(false);
            loadPages();
        }
    }, [isOpen, pdfDirectory, pageCount]);

    useEffect(() => {
        if (pages.length > 0 && imagesLoaded === pages.length) {
            setAllImagesLoaded(true);
            setTimeout(() => {
                if (displayContainer) {
                    const { scrollHeight, clientHeight } = displayContainer;
                    if (scrollHeight > clientHeight) {
                        setScrollbarHeight(displayContainer.clientHeight);
                    }
                }
                if (previewContainer) {
                    const { scrollHeight, clientHeight } = previewContainer;
                    if (scrollHeight > clientHeight) {
                        setPreviewScrollbarHeight(previewContainer.clientHeight);
                        setPreviewNeedsScroll(true);
                    }
                }
            }, 100);
        }
    }, [imagesLoaded, pages.length, displayContainer, previewContainer]);

    useEffect(() => {
        const handleScroll = () => {
            if (displayContainer && !isNavigating) {
                const { scrollTop, scrollHeight, clientHeight } = displayContainer;
                const progress = scrollTop / (scrollHeight - clientHeight);
                setScrollProgress(Math.max(0, Math.min(1, progress)));

                // Update current page based on scroll position
                const pageElements = displayContainer.querySelectorAll('[data-page]');
                const containerRect = displayContainer.getBoundingClientRect();
                const containerCenter = containerRect.top + containerRect.height / 2;

                let closestPage = 1;
                let minDistance = Infinity;

                pageElements.forEach((element) => {
                    const elementRect = element.getBoundingClientRect();
                    const elementCenter = elementRect.top + elementRect.height / 2;
                    const distance = Math.abs(elementCenter - containerCenter);

                    if (distance < minDistance) {
                        minDistance = distance;
                        closestPage = parseInt(element.getAttribute('data-page') || '1');
                    }
                });

                if (closestPage !== currentPage) {
                    setCurrentPage(closestPage);

                    // Only sync preview if not currently navigating
                    if (!isNavigating && previewContainer) {
                        const previewItems = previewContainer.querySelectorAll('.pdf-modal-preview-item');
                        const currentPreviewItem = previewItems[closestPage - 1] as HTMLElement;
                        if (currentPreviewItem) {
                            const containerRect = previewContainer.getBoundingClientRect();
                            const itemRect = currentPreviewItem.getBoundingClientRect();

                            // Only scroll if the item is not visible
                            if (itemRect.top < containerRect.top || itemRect.bottom > containerRect.bottom) {
                                currentPreviewItem.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center'
                                });
                            }
                        }
                    }
                }
            }
        };

        if (displayContainer) {
            displayContainer.addEventListener('scroll', handleScroll);
            handleScroll(); // Initialize scroll position
            return () => displayContainer.removeEventListener('scroll', handleScroll);
        }
    }, [displayContainer, isNavigating, currentPage, previewContainer]);

    const navigateToPage = useCallback((pageNumber: number, source: 'user' | 'sync') => {
        if (isNavigating || pageNumber < 1 || pageNumber > (pageCount || 1)) return;

        setIsNavigating(true);
        setCurrentPage(pageNumber);

        // Clear any existing timeout
        if (navigationTimeout) {
            clearTimeout(navigationTimeout);
        }

        // Scroll main display to page
        if (displayContainer) {
            const pageElement = displayContainer.querySelector(`[data-page="${pageNumber}"]`) as HTMLElement;
            if (pageElement) {
                pageElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        }

        // Scroll preview to show current page
        if (previewContainer && source === 'user') {
            const previewItems = previewContainer.querySelectorAll('.pdf-modal-preview-item');
            const targetPreviewItem = previewItems[pageNumber - 1] as HTMLElement;
            if (targetPreviewItem) {
                targetPreviewItem.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }

        // Reset navigation flag after animation completes
        const timeout = setTimeout(() => {
            setIsNavigating(false);
        }, 600);
        setNavigationTimeout(timeout);
    }, [isNavigating, pageCount, navigationTimeout, displayContainer, previewContainer]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                e.preventDefault();
                if (currentPage > 1) {
                    navigateToPage(currentPage - 1, 'user');
                }
            } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                e.preventDefault();
                if (currentPage < (pageCount || 1)) {
                    navigateToPage(currentPage + 1, 'user');
                }
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose, currentPage, pageCount, navigateToPage]);

    useEffect(() => {
        const handlePreviewScroll = () => {
            if (previewContainer) {
                const { scrollTop, scrollHeight, clientHeight } = previewContainer;
                const progress = scrollTop / (scrollHeight - clientHeight);
                setPreviewScrollProgress(Math.max(0, Math.min(1, progress)));

                // Check if scrolling is needed
                const needsScroll = scrollHeight > clientHeight;
                setPreviewNeedsScroll(needsScroll);
            }
        };

        if (previewContainer) {
            previewContainer.addEventListener('scroll', handlePreviewScroll);
            handlePreviewScroll(); // Initialize scroll position
            return () => previewContainer.removeEventListener('scroll', handlePreviewScroll);
        }
    }, [previewContainer]);

    useEffect(() => {
        const checkPreviewScroll = () => {
            if (previewContainer) {
                const { scrollHeight, clientHeight } = previewContainer;
                const needsScroll = scrollHeight > clientHeight;
                setPreviewNeedsScroll(needsScroll);
            }
        };

        if (previewContainer) {
            checkPreviewScroll();

            // Use ResizeObserver to detect when content changes
            const resizeObserver = new ResizeObserver(checkPreviewScroll);
            resizeObserver.observe(previewContainer);

            return () => {
                resizeObserver.disconnect();
            };
        }
    }, [previewContainer, pages]);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isDragging && displayContainer) {
                const deltaY = e.clientY - dragStartY;
                const scrollHeight = displayContainer.scrollHeight - displayContainer.clientHeight;
                const scrollbarTrackHeight = scrollbarHeight;
                const scrollRatio = deltaY / scrollbarTrackHeight;
                const newScroll = dragStartScroll + (scrollRatio * scrollHeight);
                displayContainer.scrollTop = Math.max(0, Math.min(scrollHeight, newScroll));
            }
        };

        const handleMouseUp = () => {
            setIsDragging(false);
        };

        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, dragStartY, dragStartScroll, displayContainer, scrollbarHeight]);

    useEffect(() => {
        const handlePreviewMouseMove = (e: MouseEvent) => {
            if (isPreviewDragging && previewContainer) {
                const deltaY = e.clientY - previewDragStartY;
                const scrollHeight = previewContainer.scrollHeight - previewContainer.clientHeight;
                const scrollbarTrackHeight = previewScrollbarHeight;
                const scrollRatio = deltaY / scrollbarTrackHeight;
                const newScroll = previewDragStartScroll + (scrollRatio * scrollHeight);
                previewContainer.scrollTop = Math.max(0, Math.min(scrollHeight, newScroll));
            }
        };

        const handlePreviewMouseUp = () => {
            setIsPreviewDragging(false);
        };

        if (isPreviewDragging) {
            document.addEventListener('mousemove', handlePreviewMouseMove);
            document.addEventListener('mouseup', handlePreviewMouseUp);
            return () => {
                document.removeEventListener('mousemove', handlePreviewMouseMove);
                document.removeEventListener('mouseup', handlePreviewMouseUp);
            };
        }
    }, [isPreviewDragging, previewDragStartY, previewDragStartScroll, previewContainer, previewScrollbarHeight]);

    useEffect(() => {
        return () => {
            if (navigationTimeout) {
                clearTimeout(navigationTimeout);
            }
        };
    }, [navigationTimeout]);

    const handleImageLoad = () => {
        setImagesLoaded(prev => prev + 1);
    };

    const handlePreviewImageLoad = () => {
        // Preview images don't count toward the loading state
    };

    const loadPages = async () => {
        if (!pdfDirectory || !pageCount) return;

        setLoading(true);
        setError(null);

        try {
            const pagePaths: string[] = [];
            for (let i = 1; i <= pageCount; i++) {
                pagePaths.push(`/assets/modals/pdfs/${pdfDirectory}/page${i}.jpg`);
            }
            setPages(pagePaths);
            setCurrentPage(1);
        } catch (err) {
            setError('Failed to load PDF pages');
            console.error('Error loading PDF pages:', err);
        } finally {
            setLoading(false);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            navigateToPage(currentPage - 1, 'user');
        }
    };

    const handleNextPage = () => {
        if (currentPage < (pageCount || 1)) {
            navigateToPage(currentPage + 1, 'user');
        }
    };

    const handlePageClick = (pageNumber: number) => {
        navigateToPage(pageNumber, 'user');
    };

    const handleScrollbarClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (displayContainer && !isNavigating) {
            const rect = e.currentTarget.getBoundingClientRect();
            const clickY = e.clientY - rect.top;
            const percentage = clickY / rect.height;
            const maxScroll = displayContainer.scrollHeight - displayContainer.clientHeight;
            const targetScroll = percentage * maxScroll;

            displayContainer.scrollTop = targetScroll;
        }
    };

    const handleScrollbarThumbClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    const handleThumbMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        setDragStartY(e.clientY);
        setDragStartScroll(displayContainer?.scrollTop || 0);
    };

    const handlePreviewScrollbarClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (previewContainer && !isNavigating) {
            const rect = e.currentTarget.getBoundingClientRect();
            const clickY = e.clientY - rect.top;
            const percentage = clickY / rect.height;
            const maxScroll = previewContainer.scrollHeight - previewContainer.clientHeight;
            const targetScroll = percentage * maxScroll;

            previewContainer.scrollTop = targetScroll;
        }
    };

    const handlePreviewScrollbarThumbClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
    };

    const handlePreviewThumbMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsPreviewDragging(true);
        setPreviewDragStartY(e.clientY);
        setPreviewDragStartScroll(previewContainer?.scrollTop || 0);
    };

    if (!isOpen) return null;

    return (
        <div className="pdf-modal-overlay" onClick={onClose}>
            <div className="pdf-modal-content" onClick={e => e.stopPropagation()}>
                <button className="pdf-modal-close" onClick={onClose}>
                    Close (Esc) <X className="pdf-modal-close-icon" />
                </button>

                <div className="pdf-modal-header">
                    <img
                        src="/assets/modals/icons/modal_pdf.svg"
                        alt="PDF icon"
                        className="pdf-modal-icon"
                    />
                    <div className="pdf-modal-text-container">
                        <h2 className="pdf-modal-title">{title || 'PDF Document'}</h2>
                        <p className="pdf-modal-description">{content || 'PDF content goes here'}</p>
                    </div>
                </div>

                <div className="pdf-modal-main-content">
                    <div className="pdf-modal-nav-column">
                        <div className="pdf-modal-page-info">
                            Page {currentPage} of {pageCount || 0}
                        </div>
                        <div className="pdf-modal-nav-buttons">
                            <button
                                className="pdf-modal-nav-button"
                                onClick={handlePreviousPage}
                                disabled={currentPage <= 1}
                            >
                                <ChevronLeft size={16} />
                                Previous
                            </button>
                            <button
                                className="pdf-modal-nav-button"
                                onClick={handleNextPage}
                                disabled={currentPage >= (pageCount || 1)}
                            >
                                <ChevronRight size={16} />
                                Next
                            </button>
                            <button
                                className="pdf-modal-nav-button"
                                onClick={onClose}
                            >
                                <X size={16} />
                                Close
                            </button>
                        </div>
                    </div>

                    <div className="pdf-modal-display-column">
                        <div
                            className="pdf-modal-display-container"
                            ref={setDisplayContainer}
                        >
                            {loading ? (
                                <div className="pdf-modal-loading">Loading PDF pages...</div>
                            ) : error ? (
                                <div className="pdf-modal-error">{error}</div>
                            ) : pages.length > 0 ? (
                                <>
                                    {!allImagesLoaded && (
                                        <div className="pdf-modal-loading">
                                            Loading images... ({imagesLoaded}/{pages.length})
                                        </div>
                                    )}
                                    <div className="pdf-modal-pages-stack" style={{ opacity: allImagesLoaded ? 1 : 0 }}>
                                        {pages.map((page, index) => (
                                            <img
                                                key={index}
                                                src={page}
                                                alt={`Page ${index + 1}`}
                                                className="pdf-modal-page-display"
                                                data-page={index + 1}
                                                onLoad={handleImageLoad}
                                                onError={() => {
                                                    console.error(`Failed to load image: ${page}`);
                                                    handleImageLoad();
                                                }}
                                            />
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="pdf-modal-loading">No pages available</div>
                            )}

                        </div>

                        {/* Bottom Gradient Overlay */}
                        <div className="pdf-modal-bottom-gradient" />
                    </div>

                    <div className="pdf-modal-scrollbar-column">
                        {/* Custom Scrollbar */}
                        {allImagesLoaded && pages.length > 0 && displayContainer && (
                            <div
                                className="pdf-modal-custom-scrollbar"
                                ref={(el) => {
                                    if (el) {
                                        setScrollbarHeight(el.clientHeight);
                                    }
                                }}
                            >
                                <div
                                    className="pdf-modal-scrollbar-track"
                                    onClick={handleScrollbarClick}
                                >
                                    <div
                                        className="pdf-modal-scrollbar-thumb"
                                        onClick={handleScrollbarThumbClick}
                                        onMouseDown={handleThumbMouseDown}
                                        style={{
                                            height: `${Math.max(20, (displayContainer.clientHeight / displayContainer.scrollHeight) * scrollbarHeight)}px`,
                                            top: `${scrollProgress * (scrollbarHeight - Math.max(20, (displayContainer.clientHeight / displayContainer.scrollHeight) * scrollbarHeight))}px`
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="pdf-modal-preview-column">
                        <div className="pdf-modal-preview-scroll" ref={setPreviewContainer}>
                            {pages.map((page, index) => (
                                <div
                                    key={index}
                                    className={`pdf-modal-preview-item ${index + 1 === currentPage ? 'active' : 'inactive'} ${!allImagesLoaded ? 'loading' : ''}`}
                                    onClick={() => handlePageClick(index + 1)}
                                >
                                    <img
                                        src={page}
                                        alt={`Page ${index + 1} preview`}
                                        className="pdf-modal-preview-image"
                                        onLoad={handlePreviewImageLoad}
                                        onError={() => {
                                            console.error(`Failed to load preview image: ${page}`);
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {previewNeedsScroll && allImagesLoaded && (
                        <div className="pdf-modal-preview-scrollbar-column">
                            {/* Preview Custom Scrollbar */}
                            {pages.length > 0 && displayContainer && (
                                <div
                                    className="pdf-modal-preview-custom-scrollbar"
                                    ref={(el) => {
                                        if (el) {
                                            setPreviewScrollbarHeight(el.clientHeight);
                                        }
                                    }}
                                >
                                    <div
                                        className="pdf-modal-preview-scrollbar-track"
                                        onClick={handlePreviewScrollbarClick}
                                    >
                                        <div
                                            className="pdf-modal-preview-scrollbar-thumb"
                                            onClick={handlePreviewScrollbarThumbClick}
                                            onMouseDown={handlePreviewThumbMouseDown}
                                            style={{
                                                height: `${Math.max(20, (previewContainer?.clientHeight || 0) / (previewContainer?.scrollHeight || 1) * previewScrollbarHeight)}px`,
                                                top: `${previewScrollProgress * (previewScrollbarHeight - Math.max(20, (previewContainer?.clientHeight || 0) / (previewContainer?.scrollHeight || 1) * previewScrollbarHeight))}px`
                                            }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PDFModal;